import {
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type FormEvent,
  type SetStateAction,
} from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toApiError } from '../../api/client';
import { businessApi, organisationApi, ownerPropertyApi, referenceApi } from '../../api/endpoints';
import type {
  AccommodationCategory,
  Amenity,
  BranchEmployeeWriteBody,
  Business,
  MultiBranchStatus,
  PropertyDetail,
  TenantPreference,
} from '../../api/types';
import { Checkbox, Field, PageHeader } from '../../components/Common';
import { LocationFields } from '../../components/LocationFields';
import { MultiBranchCheckout } from '../../components/MultiBranchCheckout';
import { ErrorState, InlineError, TableSkeleton } from '../../components/States';
import { categoryLabel } from '../../utils/format';
import { IconClose } from '../../components/Icons';

interface ChargeRow {
  label: string;
  amount: number;
  frequency: string;
  isMandatory: boolean;
}

/**
 * The category sections keep their fields as strings while the form is open, because a
 * half-typed number is not a number. They are converted once, on submit.
 */
interface PgForm {
  propertyType: string;
  genderPolicy: string;
  foodProvided: boolean;
  breakfastIncluded: boolean;
  lunchIncluded: boolean;
  dinnerIncluded: boolean;
  kitchenAccess: boolean;
  isVegetarianOnly: boolean;
  securityDeposit: string;
  electricityChargeType: string;
  electricityFixedAmount: string;
  maintenanceCharge: string;
  checkInRules: string;
  hasCurfew: boolean;
  curfewTime: string;
  visitorPolicy: string;
  noticePeriodDays: string;
  minimumStayMonths: string;
  hasPowerBackup: boolean;
  hasHousekeeping: boolean;
  hasLaundry: boolean;
}

interface HomeForm {
  homeType: string;
  bedrooms: string;
  bathrooms: string;
  balconies: string;
  furnishing: string;
  monthlyRent: string;
  securityDeposit: string;
  isRentNegotiable: boolean;
  maintenanceCharge: string;
  maintenanceIncludedInRent: boolean;
  electricityChargeType: string;
  waterChargeIncluded: boolean;
  availableFrom: string;
  floorNumber: string;
  totalFloors: string;
  carParkingCount: string;
  bikeParkingCount: string;
  isCoveredParking: boolean;
  carpetAreaSqft: string;
  hasLift: boolean;
  hasPowerBackup: boolean;
  tenantPreferenceNotes: string;
  tenantPreferenceIds: string[];
}

interface HotelForm {
  hotelType: string;
  starRating: string;
  checkInTime: string;
  checkOutTime: string;
  is24HourReception: boolean;
  receptionHours: string;
  cancellationPolicy: string;
  childPolicy: string;
  petsAllowed: boolean;
  couplesAllowed: boolean;
  localIdAccepted: boolean;
  hasRestaurant: boolean;
  hasRoomService: boolean;
  hasParking: boolean;
  hasConferenceRoom: boolean;
}

/**
 * Create and edit form for all three categories. The category is chosen once, at
 * creation: changing it later would orphan the category detail row, the room inventory
 * and the billing record, so the API refuses it and the field is locked here too.
 */
export function ListingEditorPage({
  homeRentalOnly = false,
  branchCreation = false,
}: {
  homeRentalOnly?: boolean;
  branchCreation?: boolean;
}) {
  const { propertyId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const isEdit = Boolean(propertyId);

  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [preferences, setPreferences] = useState<TenantPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  // Set when saving was refused because the organisation needs the yearly multi-branch
  // subscription. That is a purchase the owner can make here and now, not a form error,
  // so it opens the checkout rather than printing a dead end.
  const [entitlementNeeded, setEntitlementNeeded] = useState<MultiBranchStatus | null>(null);

  // --- Shared fields ----------------------------------------------------------------
  const [businessId, setBusinessId] = useState(params.get('businessId') ?? '');
  const [category, setCategory] = useState<AccommodationCategory>(
    homeRentalOnly
      ? 'HomeRental'
      : (params.get('category') as AccommodationCategory) || 'PgHostel',
  );
  const [branchName, setBranchName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [landmark, setLandmark] = useState('');
  const [locality, setLocality] = useState('');
  const [city, setCity] = useState('Chennai');
  const [state, setState] = useState('Tamil Nadu');
  const [pincode, setPincode] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [contactPersonName, setContactPersonName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [whatsAppNumber, setWhatsAppNumber] = useState('');
  const [amenityIds, setAmenityIds] = useState<string[]>([]);
  const [charges, setCharges] = useState<ChargeRow[]>([]);
  const [withEmployee, setWithEmployee] = useState(false);
  const [employeeName, setEmployeeName] = useState('');
  const [employeeUsername, setEmployeeUsername] = useState('');
  const [employeePassword, setEmployeePassword] = useState('');

  // --- PG --------------------------------------------------------------------------
  const [pg, setPg] = useState<PgForm>({
    propertyType: 'Hostel',
    genderPolicy: 'Unisex',
    foodProvided: false,
    breakfastIncluded: false,
    lunchIncluded: false,
    dinnerIncluded: false,
    kitchenAccess: false,
    isVegetarianOnly: false,
    securityDeposit: '',
    electricityChargeType: 'ActualBillShared',
    electricityFixedAmount: '',
    maintenanceCharge: '',
    checkInRules: '',
    hasCurfew: false,
    curfewTime: '22:00',
    visitorPolicy: '',
    noticePeriodDays: '30',
    minimumStayMonths: '',
    hasPowerBackup: false,
    hasHousekeeping: false,
    hasLaundry: false,
  });

  // --- Home -------------------------------------------------------------------------
  const [home, setHome] = useState<HomeForm>({
    homeType: 'Apartment',
    bedrooms: '2',
    bathrooms: '2',
    balconies: '0',
    furnishing: 'Unfurnished',
    monthlyRent: '',
    securityDeposit: '',
    isRentNegotiable: false,
    maintenanceCharge: '',
    maintenanceIncludedInRent: false,
    electricityChargeType: 'ActualBillShared',
    waterChargeIncluded: true,
    availableFrom: '',
    floorNumber: '',
    totalFloors: '',
    carParkingCount: '0',
    bikeParkingCount: '0',
    isCoveredParking: false,
    carpetAreaSqft: '',
    hasLift: false,
    hasPowerBackup: false,
    tenantPreferenceNotes: '',
    tenantPreferenceIds: [],
  });

  // --- Hotel ------------------------------------------------------------------------
  const [hotel, setHotel] = useState<HotelForm>({
    hotelType: 'Budget',
    starRating: '',
    checkInTime: '12:00',
    checkOutTime: '11:00',
    is24HourReception: false,
    receptionHours: '',
    cancellationPolicy: '',
    childPolicy: '',
    petsAllowed: false,
    couplesAllowed: true,
    localIdAccepted: true,
    hasRestaurant: false,
    hasRoomService: false,
    hasParking: false,
    hasConferenceRoom: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const [businessList, preferenceList] = await Promise.all([
        businessApi.list(),
        referenceApi.tenantPreferences(),
      ]);

      const editableBusinesses = homeRentalOnly
        ? businessList.filter(
            (business) =>
              business.canManageProperties &&
              business.categories.includes('HomeRental'),
          )
        : branchCreation
          ? businessList.filter((business) =>
              business.categories.some((value) => value === 'PgHostel' || value === 'Hotel'),
            )
          : businessList;
      setBusinesses(editableBusinesses);
      setPreferences(preferenceList);

      if (propertyId) {
        const detail = await ownerPropertyApi.get(propertyId);
        hydrate(detail);
      } else if (editableBusinesses.length > 0 && !businessId) {
        setBusinessId(editableBusinesses[0].id);
        setContactPhone(editableBusinesses[0].contactPhone);
        setContactEmail(editableBusinesses[0].contactEmail ?? '');
      }
      if (branchCreation && editableBusinesses.length > 0) {
        const selected = editableBusinesses.find((business) => business.id === businessId) ?? editableBusinesses[0];
        if (!selected.categories.includes(category)) {
          setCategory(selected.categories.find((value) => value === 'PgHostel' || value === 'Hotel') ?? 'PgHostel');
        }
      }
    } catch (err) {
      setLoadError(toApiError(err).message);
    } finally {
      setLoading(false);
    }
    // hydrate and businessId are intentionally excluded: this runs once per property id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, homeRentalOnly, branchCreation]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    referenceApi.amenities(category).then(setAmenities).catch(() => setAmenities([]));
  }, [category]);

  function hydrate(detail: PropertyDetail) {
    setBusinessId(detail.businessId);
    setCategory(detail.category);
    setBranchName(detail.branchName ?? '');
    setTitle(detail.title);
    setDescription(detail.description ?? '');
    setAddressLine1(detail.addressLine1);
    setAddressLine2(detail.addressLine2 ?? '');
    setLandmark(detail.landmark ?? '');
    setLocality(detail.locality);
    setCity(detail.city);
    setState(detail.state);
    setPincode(detail.pincode);
    setLatitude(detail.latitude?.toString() ?? '');
    setLongitude(detail.longitude?.toString() ?? '');
    setContactPersonName(detail.contact.name ?? '');
    setContactPhone(detail.contact.phone);
    setContactEmail(detail.contact.email ?? '');
    setWhatsAppNumber(detail.contact.whatsAppNumber ?? '');
    setAmenityIds(detail.amenities.map((a) => a.id));
    setCharges(
      detail.charges.map((c) => ({
        label: c.label,
        amount: c.amount,
        frequency: c.frequency,
        isMandatory: c.isMandatory,
      })),
    );

    if (detail.pgDetail) {
      const d = detail.pgDetail;
      setPg({
        propertyType: d.propertyType,
        genderPolicy: d.genderPolicy,
        foodProvided: d.foodProvided,
        breakfastIncluded: d.breakfastIncluded,
        lunchIncluded: d.lunchIncluded,
        dinnerIncluded: d.dinnerIncluded,
        kitchenAccess: d.kitchenAccess,
        isVegetarianOnly: d.isVegetarianOnly,
        securityDeposit: d.securityDeposit?.toString() ?? '',
        electricityChargeType: d.electricityChargeType,
        electricityFixedAmount: d.electricityFixedAmount?.toString() ?? '',
        maintenanceCharge: d.maintenanceCharge?.toString() ?? '',
        checkInRules: d.checkInRules ?? '',
        hasCurfew: d.hasCurfew,
        curfewTime: d.curfewTime?.slice(0, 5) ?? '22:00',
        visitorPolicy: d.visitorPolicy ?? '',
        noticePeriodDays: d.noticePeriodDays?.toString() ?? '',
        minimumStayMonths: d.minimumStayMonths?.toString() ?? '',
        hasPowerBackup: d.hasPowerBackup,
        hasHousekeeping: d.hasHousekeeping,
        hasLaundry: d.hasLaundry,
      });
    }

    if (detail.homeDetail) {
      const d = detail.homeDetail;
      setHome({
        homeType: d.homeType,
        bedrooms: String(d.bedrooms),
        bathrooms: String(d.bathrooms),
        balconies: String(d.balconies),
        furnishing: d.furnishing,
        monthlyRent: String(d.monthlyRent),
        securityDeposit: String(d.securityDeposit),
        isRentNegotiable: d.isRentNegotiable,
        maintenanceCharge: d.maintenanceCharge?.toString() ?? '',
        maintenanceIncludedInRent: d.maintenanceIncludedInRent,
        electricityChargeType: d.electricityChargeType,
        waterChargeIncluded: d.waterChargeIncluded,
        availableFrom: d.availableFrom ?? '',
        floorNumber: d.floorNumber?.toString() ?? '',
        totalFloors: d.totalFloors?.toString() ?? '',
        carParkingCount: String(d.carParkingCount),
        bikeParkingCount: String(d.bikeParkingCount),
        isCoveredParking: d.isCoveredParking,
        carpetAreaSqft: d.carpetAreaSqft?.toString() ?? '',
        hasLift: d.hasLift,
        hasPowerBackup: d.hasPowerBackup,
        tenantPreferenceNotes: d.tenantPreferenceNotes ?? '',
        tenantPreferenceIds: d.tenantPreferences.map((p) => p.id),
      });
    }

    if (detail.hotelDetail) {
      const d = detail.hotelDetail;
      setHotel({
        hotelType: d.hotelType,
        starRating: d.starRating?.toString() ?? '',
        checkInTime: d.checkInTime.slice(0, 5),
        checkOutTime: d.checkOutTime.slice(0, 5),
        is24HourReception: d.is24HourReception,
        receptionHours: d.receptionHours ?? '',
        cancellationPolicy: d.cancellationPolicy ?? '',
        childPolicy: d.childPolicy ?? '',
        petsAllowed: d.petsAllowed,
        couplesAllowed: d.couplesAllowed,
        localIdAccepted: d.localIdAccepted,
        hasRestaurant: d.hasRestaurant,
        hasRoomService: d.hasRoomService,
        hasParking: d.hasParking,
        hasConferenceRoom: d.hasConferenceRoom,
      });
    }
  }

  const number = (value: string) => (value.trim() === '' ? undefined : Number(value));

  const save = async () => {
    setSaving(true);
    setError(null);
    setFieldErrors({});

    const body: Record<string, unknown> = {
      businessId,
      category,
      branchName: category === 'HomeRental' ? undefined : branchName.trim() || undefined,
      title: title.trim(),
      description: description.trim() || undefined,
      addressLine1: addressLine1.trim(),
      addressLine2: addressLine2.trim() || undefined,
      landmark: landmark.trim() || undefined,
      locality: locality.trim(),
      city: city.trim(),
      state: state.trim(),
      pincode: pincode.trim(),
      latitude: number(latitude),
      longitude: number(longitude),
      contactPersonName: contactPersonName.trim() || undefined,
      contactPhone: contactPhone.trim(),
      contactEmail: contactEmail.trim() || undefined,
      whatsAppNumber: whatsAppNumber.trim() || undefined,
      amenityIds,
      charges: charges
        .filter((c) => c.label.trim())
        .map((c, index) => ({
          label: c.label.trim(),
          amount: c.amount,
          frequency: c.frequency,
          isMandatory: c.isMandatory,
          displayOrder: (index + 1) * 10,
        })),
    };

    if (category === 'PgHostel') {
      body.pgDetail = {
        ...pg,
        securityDeposit: number(pg.securityDeposit),
        electricityFixedAmount: number(pg.electricityFixedAmount),
        maintenanceCharge: number(pg.maintenanceCharge),
        noticePeriodDays: number(pg.noticePeriodDays),
        minimumStayMonths: number(pg.minimumStayMonths),
        curfewTime: pg.hasCurfew ? `${pg.curfewTime}:00` : undefined,
        checkInRules: pg.checkInRules.trim() || undefined,
        visitorPolicy: pg.visitorPolicy.trim() || undefined,
      };
    } else if (category === 'HomeRental') {
      body.homeDetail = {
        ...home,
        bedrooms: Number(home.bedrooms || 0),
        bathrooms: Number(home.bathrooms || 0),
        balconies: Number(home.balconies || 0),
        monthlyRent: Number(home.monthlyRent || 0),
        securityDeposit: Number(home.securityDeposit || 0),
        maintenanceCharge: number(home.maintenanceCharge),
        floorNumber: number(home.floorNumber),
        totalFloors: number(home.totalFloors),
        carParkingCount: Number(home.carParkingCount || 0),
        bikeParkingCount: Number(home.bikeParkingCount || 0),
        carpetAreaSqft: number(home.carpetAreaSqft),
        availableFrom: home.availableFrom || undefined,
        tenantPreferenceNotes: home.tenantPreferenceNotes.trim() || undefined,
      };
    } else {
      body.hotelDetail = {
        ...hotel,
        starRating: number(hotel.starRating),
        checkInTime: `${hotel.checkInTime}:00`,
        checkOutTime: `${hotel.checkOutTime}:00`,
        receptionHours: hotel.receptionHours.trim() || undefined,
        cancellationPolicy: hotel.cancellationPolicy.trim() || undefined,
        childPolicy: hotel.childPolicy.trim() || undefined,
      };
    }

    try {
      const employee: BranchEmployeeWriteBody | undefined =
        branchCreation && withEmployee
          ? {
              fullName: employeeName.trim(),
              username: employeeUsername.trim(),
              password: employeePassword,
              canManageProperties: true,
              canPublishListings: false,
            }
          : undefined;
      const saved = propertyId
        ? await ownerPropertyApi.update(propertyId, body)
        : branchCreation
          ? await organisationApi.createBranch(businessId, body, employee)
          : await ownerPropertyApi.create(body);

      navigate(`/owner/listings/${saved.id}`);
    } catch (err) {
      const apiError = toApiError(err);

      // A new branch refused because the organisation is not entitled to one is not a
      // problem with the form: the owner can buy the yearly subscription and the same
      // save will then succeed. Ask the server what the position actually is rather
      // than matching on the message text, and offer the checkout when it says so.
      // Home rentals are listings rather than branches and are never gated.
      if (!propertyId && category !== 'HomeRental' && apiError.kind === 'conflict' && businessId) {
        const status = await organisationApi.multiBranch(businessId).catch(() => null);

        if (status && !status.canAddBranch && status.reason === 'EntitlementRequired') {
          setEntitlementNeeded(status);
          return;
        }
      }

      setError(apiError.message);
      setFieldErrors(apiError.fieldErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await save();
  };

  if (loading) return <TableSkeleton rows={8} columns={2} />;
  if (loadError) return <ErrorState message={loadError} onRetry={load} />;

  if (businesses.length === 0) {
    return (
      <ErrorState
        title={branchCreation ? 'No business set up for branches' : 'Create a business first'}
        message={
          branchCreation
            ? 'A business with a PG / Hostel or Hotel category is required before you can create a branch.'
            : 'Listings sit under a business. Create one, then come back here.'
        }
      />
    );
  }

  const selectedBusiness = businesses.find((b) => b.id === businessId);
  const availableCategories = selectedBusiness?.categories ?? [];
  const creatableCategories = branchCreation
    ? availableCategories.filter((value) => value === 'PgHostel' || value === 'Hotel')
    : availableCategories;

  return (
    <form onSubmit={submit} noValidate>
      <PageHeader
        title={isEdit ? 'Edit listing' : branchCreation ? 'Create a branch' : 'Add a property'}
        actions={
          <Link to={branchCreation ? '/owner/branches' : '/owner/listings'} className="btn btn-sm btn-outline-secondary">
            Cancel
          </Link>
        }
      />

      <InlineError message={error} />

      {entitlementNeeded && (
        <MultiBranchCheckout
          businessId={businessId}
          organisationName={selectedBusiness?.name}
          status={entitlementNeeded}
          // Retries the very save that was refused, so the owner lands on the branch they
          // were creating instead of having to fill the form again.
          onActivated={async () => {
            setEntitlementNeeded(null);
            await save();
          }}
          onCancel={() => setEntitlementNeeded(null)}
        />
      )}

      <section className="sf-card sf-card-pad mb-3">
        <h3 className="sf-section-title">Basics</h3>

        <Field label="Business" htmlFor="editor-business" required>
          <select
            id="editor-business"
            className="form-select"
            value={businessId}
            onChange={(e) => {
              setBusinessId(e.target.value);
              const next = businesses.find((b) => b.id === e.target.value);
              if (next && !contactPhone) setContactPhone(next.contactPhone);
              if (next && branchCreation && !next.categories.includes(category)) {
                setCategory(next.categories.find((value) => value === 'PgHostel' || value === 'Hotel') ?? 'PgHostel');
              }
            }}
            disabled={isEdit || homeRentalOnly}
            required
          >
            {businesses.map((business) => (
              <option key={business.id} value={business.id}>
                {business.name}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Category"
          htmlFor="editor-category"
          required
          hint={isEdit ? 'The category cannot be changed after a listing is created.' : undefined}
          error={fieldErrors.category?.[0]}
        >
          <select
            id="editor-category"
            className="form-select"
            value={category}
            onChange={(e) => setCategory(e.target.value as AccommodationCategory)}
            disabled={isEdit}
            required
          >
            {(homeRentalOnly
              ? (['HomeRental'] as AccommodationCategory[])
              : branchCreation
                ? creatableCategories
                : availableCategories.length > 0
                  ? availableCategories
                : (['PgHostel'] as AccommodationCategory[])
            ).map(
              (value) => (
                <option key={value} value={value}>
                  {categoryLabel(value)}
                </option>
              ),
            )}
          </select>
        </Field>

        {category !== 'HomeRental' && (
          <Field
            label="Branch name"
            htmlFor="editor-branch"
            hint="For example Velachery or OMR. Shown next to your brand name."
          >
            <input
              id="editor-branch"
              className="form-control"
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              maxLength={150}
            />
          </Field>
        )}

        <Field label="Listing title" htmlFor="editor-title" required error={fieldErrors.title?.[0]}>
          <input
            id="editor-title"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            minLength={5}
            maxLength={250}
          />
        </Field>

        <Field
          label="Description"
          htmlFor="editor-description"
          required
          hint="At least 30 characters. This is what customers read first."
          error={fieldErrors.description?.[0]}
        >
          <textarea
            id="editor-description"
            className="form-control"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={6000}
          />
        </Field>
      </section>

      <section className="sf-card sf-card-pad mb-3">
        <h3 className="sf-section-title">Address</h3>

        <Field label="Address line 1" htmlFor="editor-address1" required error={fieldErrors.addressLine1?.[0]}>
          <input
            id="editor-address1"
            className="form-control"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            required
            maxLength={250}
          />
        </Field>

        <Field label="Address line 2" htmlFor="editor-address2">
          <input
            id="editor-address2"
            className="form-control"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
            maxLength={250}
          />
        </Field>

        <div className="row">
          <div className="col-12 col-md-6">
            <Field label="Locality" htmlFor="editor-locality" required error={fieldErrors.locality?.[0]}>
              <input
                id="editor-locality"
                className="form-control"
                value={locality}
                onChange={(e) => setLocality(e.target.value)}
                required
                maxLength={150}
              />
            </Field>
          </div>
          <div className="col-12 col-md-6">
            <Field label="Pincode" htmlFor="editor-pincode" required error={fieldErrors.pincode?.[0]}>
              <input
                id="editor-pincode"
                className="form-control"
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
                required
                pattern="\d{6}"
                maxLength={6}
              />
            </Field>
          </div>
        </div>

        <LocationFields
          idPrefix="editor-location"
          city={city}
          state={state}
          onCityChange={setCity}
          onStateChange={setState}
        />

        <Field label="Landmark" htmlFor="editor-landmark">
          <input
            id="editor-landmark"
            className="form-control"
            value={landmark}
            onChange={(e) => setLandmark(e.target.value)}
            maxLength={200}
          />
        </Field>

        <div className="row">
          <div className="col-12 col-md-6">
            <Field
              label="Latitude"
              htmlFor="editor-lat"
              hint="Optional, but without it your listing will not appear in distance searches."
              error={fieldErrors.latitude?.[0]}
            >
              <input
                id="editor-lat"
                className="form-control"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                inputMode="decimal"
              />
            </Field>
          </div>
          <div className="col-12 col-md-6">
            <Field label="Longitude" htmlFor="editor-lng">
              <input
                id="editor-lng"
                className="form-control"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                inputMode="decimal"
              />
            </Field>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() =>
            navigator.geolocation?.getCurrentPosition((position) => {
              setLatitude(position.coords.latitude.toFixed(6));
              setLongitude(position.coords.longitude.toFixed(6));
            })
          }
        >
          Use my current location
        </button>
      </section>

      <section className="sf-card sf-card-pad mb-3">
        <h3 className="sf-section-title">Contact for enquiries</h3>
        <p className="sf-muted small">
          This is what customers see. Use a number someone will answer.
        </p>

        <div className="row">
          <div className="col-12 col-md-6">
            <Field label="Contact name" htmlFor="editor-contact-name">
              <input
                id="editor-contact-name"
                className="form-control"
                value={contactPersonName}
                onChange={(e) => setContactPersonName(e.target.value)}
              />
            </Field>
          </div>
          <div className="col-12 col-md-6">
            <Field label="Phone" htmlFor="editor-contact-phone" required error={fieldErrors.contactPhone?.[0]}>
              <input
                id="editor-contact-phone"
                className="form-control"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                required
              />
            </Field>
          </div>
          <div className="col-12 col-md-6">
            <Field label="Email" htmlFor="editor-contact-email">
              <input
                id="editor-contact-email"
                className="form-control"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </Field>
          </div>
          <div className="col-12 col-md-6">
            <Field label="WhatsApp" htmlFor="editor-whatsapp" hint="Defaults to the contact phone.">
              <input
                id="editor-whatsapp"
                className="form-control"
                type="tel"
                value={whatsAppNumber}
                onChange={(e) => setWhatsAppNumber(e.target.value)}
              />
            </Field>
          </div>
        </div>
      </section>

      {category === 'PgHostel' && <PgSection pg={pg} setPg={setPg} errors={fieldErrors} />}
      {category === 'HomeRental' && (
        <HomeSection home={home} setHome={setHome} preferences={preferences} errors={fieldErrors} />
      )}
      {category === 'Hotel' && <HotelSection hotel={hotel} setHotel={setHotel} />}

      <ChargesSection charges={charges} setCharges={setCharges} category={category} />

      <section className="sf-card sf-card-pad mb-3">
        <h3 className="sf-section-title">Amenities</h3>

        {Object.entries(
          amenities.reduce<Record<string, Amenity[]>>((groups, amenity) => {
            (groups[amenity.group] ??= []).push(amenity);
            return groups;
          }, {}),
        ).map(([group, items]) => (
          <div className="mb-3" key={group}>
            <h4 className="small text-uppercase sf-muted fw-bold">{group}</h4>
            <div className="row">
              {items.map((amenity) => (
                <div className="col-12 col-sm-6 col-lg-4" key={amenity.id}>
                  <Checkbox
                    id={`amenity-${amenity.id}`}
                    label={amenity.name}
                    checked={amenityIds.includes(amenity.id)}
                    onChange={(checked) =>
                      setAmenityIds((previous) =>
                        checked ? [...previous, amenity.id] : previous.filter((id) => id !== amenity.id),
                      )
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {branchCreation && (
        <section className="sf-card sf-card-pad mb-3">
          <div className="form-check mb-3">
            <input
              id="branch-with-employee"
              className="form-check-input"
              type="checkbox"
              checked={withEmployee}
              onChange={(event) => setWithEmployee(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="branch-with-employee">
              Create a front-desk login for this branch
            </label>
            <div className="form-text">
              Staff can manage this branch. You can add or update staff access later.
            </div>
          </div>
          {withEmployee && (
            <>
              <Field label="Staff name" htmlFor="branch-employee-name" required>
                <input
                  id="branch-employee-name"
                  className="form-control"
                  value={employeeName}
                  onChange={(event) => setEmployeeName(event.target.value)}
                  required
                />
              </Field>
              <Field
                label="Username"
                htmlFor="branch-employee-username"
                required
                hint="Use 3–80 letters, numbers, dots, underscores or hyphens; unique within your organisation."
              >
                <input
                  id="branch-employee-username"
                  className="form-control"
                  value={employeeUsername}
                  onChange={(event) => setEmployeeUsername(event.target.value)}
                  autoComplete="off"
                  minLength={3}
                  required
                />
              </Field>
              <Field
                label="Password"
                htmlFor="branch-employee-password"
                required
                hint="At least 8 characters, including an uppercase letter, a lowercase letter, and a digit."
              >
                <input
                  id="branch-employee-password"
                  type="password"
                  className="form-control"
                  value={employeePassword}
                  onChange={(event) => setEmployeePassword(event.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </Field>
            </>
          )}
        </section>
      )}

      <div className="d-flex gap-2 mb-5">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save changes' : branchCreation ? 'Create branch' : 'Create listing'}
        </button>
        <Link to={branchCreation ? '/owner/branches' : '/owner/listings'} className="btn btn-outline-secondary">
          Cancel
        </Link>
      </div>

      <p className="sf-muted small">
        Saving keeps this as a draft. You add photos and rooms next, then submit it for publication.
      </p>
    </form>
  );
}

// =====================================================================================
// Category sections
// =====================================================================================

function PgSection({
  pg,
  setPg,
  errors,
}: {
  pg: PgForm;
  setPg: Dispatch<SetStateAction<PgForm>>;
  errors: Record<string, string[]>;
}) {
  const set = <K extends keyof PgForm>(key: K, value: PgForm[K]) =>
    setPg((previous) => ({ ...previous, [key]: value }));

  return (
    <section className="sf-card sf-card-pad mb-3">
      <h3 className="sf-section-title">PG &amp; hostel details</h3>

      <div className="row">
        <div className="col-12 col-md-6">
          <Field label="Property type" htmlFor="pg-type" required>
            <select
              id="pg-type"
              className="form-select"
              value={pg.propertyType}
              onChange={(e) => set('propertyType', e.target.value)}
            >
              <option value="PayingGuest">Paying guest</option>
              <option value="Hostel">Hostel</option>
              <option value="CoLiving">Co-living</option>
              <option value="Other">Other</option>
            </select>
          </Field>
        </div>

        <div className="col-12 col-md-6">
          <Field label="Accommodation for" htmlFor="pg-gender" required>
            <select
              id="pg-gender"
              className="form-select"
              value={pg.genderPolicy}
              onChange={(e) => set('genderPolicy', e.target.value)}
            >
              <option value="Boys">Men</option>
              <option value="Girls">Women</option>
              <option value="Unisex">Unisex</option>
            </select>
          </Field>
        </div>
      </div>

      <fieldset className="mb-3">
        <legend className="form-label">Food</legend>
        <Checkbox id="pg-food" label="Food is provided" checked={pg.foodProvided} onChange={(v) => set('foodProvided', v)} />
        {pg.foodProvided && (
          <div className="ps-3">
            <Checkbox id="pg-breakfast" label="Breakfast" checked={pg.breakfastIncluded} onChange={(v) => set('breakfastIncluded', v)} />
            <Checkbox id="pg-lunch" label="Lunch" checked={pg.lunchIncluded} onChange={(v) => set('lunchIncluded', v)} />
            <Checkbox id="pg-dinner" label="Dinner" checked={pg.dinnerIncluded} onChange={(v) => set('dinnerIncluded', v)} />
            <Checkbox id="pg-veg" label="Vegetarian only" checked={pg.isVegetarianOnly} onChange={(v) => set('isVegetarianOnly', v)} />
          </div>
        )}
        <Checkbox id="pg-kitchen" label="Residents can use the kitchen" checked={pg.kitchenAccess} onChange={(v) => set('kitchenAccess', v)} />
      </fieldset>

      <div className="row">
        <div className="col-12 col-md-6">
          <Field label="Default security deposit" htmlFor="pg-deposit" hint="Room types can override this.">
            <input
              id="pg-deposit"
              className="form-control"
              inputMode="numeric"
              value={pg.securityDeposit}
              onChange={(e) => set('securityDeposit', e.target.value)}
            />
          </Field>
        </div>

        <div className="col-12 col-md-6">
          <Field label="Electricity" htmlFor="pg-electricity">
            <select
              id="pg-electricity"
              className="form-select"
              value={pg.electricityChargeType}
              onChange={(e) => set('electricityChargeType', e.target.value)}
            >
              <option value="IncludedInRent">Included in rent</option>
              <option value="ActualBillShared">Actual bill, shared</option>
              <option value="FixedPerMonth">Fixed amount per month</option>
              <option value="PerUnit">Charged per unit</option>
            </select>
          </Field>
        </div>

        {pg.electricityChargeType === 'FixedPerMonth' && (
          <div className="col-12 col-md-6">
            <Field label="Fixed electricity amount" htmlFor="pg-electricity-amount">
              <input
                id="pg-electricity-amount"
                className="form-control"
                inputMode="numeric"
                value={pg.electricityFixedAmount}
                onChange={(e) => set('electricityFixedAmount', e.target.value)}
              />
            </Field>
          </div>
        )}

        <div className="col-12 col-md-6">
          <Field label="Maintenance charge per month" htmlFor="pg-maintenance">
            <input
              id="pg-maintenance"
              className="form-control"
              inputMode="numeric"
              value={pg.maintenanceCharge}
              onChange={(e) => set('maintenanceCharge', e.target.value)}
            />
          </Field>
        </div>

        <div className="col-12 col-md-6">
          <Field label="Minimum stay (months)" htmlFor="pg-min-stay">
            <input
              id="pg-min-stay"
              className="form-control"
              inputMode="numeric"
              value={pg.minimumStayMonths}
              onChange={(e) => set('minimumStayMonths', e.target.value)}
            />
          </Field>
        </div>

        <div className="col-12 col-md-6">
          <Field label="Notice period (days)" htmlFor="pg-notice">
            <input
              id="pg-notice"
              className="form-control"
              inputMode="numeric"
              value={pg.noticePeriodDays}
              onChange={(e) => set('noticePeriodDays', e.target.value)}
            />
          </Field>
        </div>
      </div>

      <Checkbox id="pg-curfew" label="There is a curfew" checked={pg.hasCurfew} onChange={(v) => set('hasCurfew', v)} />

      {pg.hasCurfew && (
        <Field label="Curfew time" htmlFor="pg-curfew-time" required error={errors['pgDetail.CurfewTime']?.[0]}>
          <input
            id="pg-curfew-time"
            type="time"
            className="form-control"
            style={{ maxWidth: 160 }}
            value={pg.curfewTime}
            onChange={(e) => set('curfewTime', e.target.value)}
          />
        </Field>
      )}

      <Field label="Visitor policy" htmlFor="pg-visitors">
        <input
          id="pg-visitors"
          className="form-control"
          value={pg.visitorPolicy}
          onChange={(e) => set('visitorPolicy', e.target.value)}
          maxLength={1000}
        />
      </Field>

      <Field label="Rules at check-in" htmlFor="pg-checkin" hint="Documents needed, deposit terms, and so on.">
        <textarea
          id="pg-checkin"
          className="form-control"
          rows={2}
          value={pg.checkInRules}
          onChange={(e) => set('checkInRules', e.target.value)}
          maxLength={2000}
        />
      </Field>

      <div className="row">
        <div className="col-12 col-sm-4">
          <Checkbox id="pg-power" label="Power backup" checked={pg.hasPowerBackup} onChange={(v) => set('hasPowerBackup', v)} />
        </div>
        <div className="col-12 col-sm-4">
          <Checkbox id="pg-housekeeping" label="Housekeeping" checked={pg.hasHousekeeping} onChange={(v) => set('hasHousekeeping', v)} />
        </div>
        <div className="col-12 col-sm-4">
          <Checkbox id="pg-laundry" label="Laundry" checked={pg.hasLaundry} onChange={(v) => set('hasLaundry', v)} />
        </div>
      </div>
    </section>
  );
}

function HomeSection({
  home,
  setHome,
  preferences,
  errors,
}: {
  home: HomeForm;
  setHome: Dispatch<SetStateAction<HomeForm>>;
  preferences: TenantPreference[];
  errors: Record<string, string[]>;
}) {
  const set = <K extends keyof HomeForm>(key: K, value: HomeForm[K]) =>
    setHome((previous) => ({ ...previous, [key]: value }));

  const selectedPreferences = home.tenantPreferenceIds;

  return (
    <section className="sf-card sf-card-pad mb-3">
      <h3 className="sf-section-title">Home details</h3>

      <div className="row">
        <div className="col-12 col-md-6">
          <Field label="Property type" htmlFor="home-type" required>
            <select
              id="home-type"
              className="form-select"
              value={home.homeType}
              onChange={(e) => set('homeType', e.target.value)}
            >
              <option value="Apartment">Apartment</option>
              <option value="IndependentHouse">Independent house</option>
              <option value="Villa">Villa</option>
              <option value="StudioApartment">Studio apartment</option>
              <option value="Other">Other</option>
            </select>
          </Field>
        </div>

        <div className="col-12 col-md-6">
          <Field label="Furnishing" htmlFor="home-furnishing" required>
            <select
              id="home-furnishing"
              className="form-select"
              value={home.furnishing}
              onChange={(e) => set('furnishing', e.target.value)}
            >
              <option value="Unfurnished">Unfurnished</option>
              <option value="SemiFurnished">Semi furnished</option>
              <option value="FullyFurnished">Fully furnished</option>
            </select>
          </Field>
        </div>

        <div className="col-6 col-md-3">
          <Field label="Bedrooms (BHK)" htmlFor="home-bedrooms" required error={errors.bedrooms?.[0]}>
            <input
              id="home-bedrooms"
              className="form-control"
              inputMode="numeric"
              value={home.bedrooms}
              onChange={(e) => set('bedrooms', e.target.value)}
              required
            />
          </Field>
        </div>

        <div className="col-6 col-md-3">
          <Field label="Bathrooms" htmlFor="home-bathrooms" required>
            <input
              id="home-bathrooms"
              className="form-control"
              inputMode="numeric"
              value={home.bathrooms}
              onChange={(e) => set('bathrooms', e.target.value)}
              required
            />
          </Field>
        </div>

        <div className="col-6 col-md-3">
          <Field label="Balconies" htmlFor="home-balconies">
            <input
              id="home-balconies"
              className="form-control"
              inputMode="numeric"
              value={home.balconies}
              onChange={(e) => set('balconies', e.target.value)}
            />
          </Field>
        </div>

        <div className="col-6 col-md-3">
          <Field label="Carpet area (sq ft)" htmlFor="home-area">
            <input
              id="home-area"
              className="form-control"
              inputMode="numeric"
              value={home.carpetAreaSqft}
              onChange={(e) => set('carpetAreaSqft', e.target.value)}
            />
          </Field>
        </div>

        <div className="col-12 col-md-6">
          <Field label="Monthly rent" htmlFor="home-rent" required error={errors.monthlyRent?.[0]}>
            <input
              id="home-rent"
              className="form-control"
              inputMode="numeric"
              value={home.monthlyRent}
              onChange={(e) => set('monthlyRent', e.target.value)}
              required
            />
          </Field>
        </div>

        <div className="col-12 col-md-6">
          <Field label="Security deposit" htmlFor="home-deposit" required>
            <input
              id="home-deposit"
              className="form-control"
              inputMode="numeric"
              value={home.securityDeposit}
              onChange={(e) => set('securityDeposit', e.target.value)}
              required
            />
          </Field>
        </div>
      </div>

      <Checkbox
        id="home-negotiable"
        label="Rent is negotiable"
        checked={home.isRentNegotiable}
        onChange={(v) => set('isRentNegotiable', v)}
      />
      <Checkbox
        id="home-maintenance-included"
        label="Maintenance is included in the rent"
        checked={home.maintenanceIncludedInRent}
        onChange={(v) => set('maintenanceIncludedInRent', v)}
      />

      {!home.maintenanceIncludedInRent && (
        <Field label="Maintenance charge per month" htmlFor="home-maintenance">
          <input
            id="home-maintenance"
            className="form-control"
            inputMode="numeric"
            value={home.maintenanceCharge}
            onChange={(e) => set('maintenanceCharge', e.target.value)}
          />
        </Field>
      )}

      <Checkbox
        id="home-water"
        label="Water charges are included"
        checked={home.waterChargeIncluded}
        onChange={(v) => set('waterChargeIncluded', v)}
      />

      <div className="row">
        <div className="col-12 col-md-4">
          <Field label="Available from" htmlFor="home-available">
            <input
              id="home-available"
              type="date"
              className="form-control"
              value={home.availableFrom}
              onChange={(e) => set('availableFrom', e.target.value)}
            />
          </Field>
        </div>
        <div className="col-6 col-md-4">
          <Field label="Floor number" htmlFor="home-floor">
            <input
              id="home-floor"
              className="form-control"
              inputMode="numeric"
              value={home.floorNumber}
              onChange={(e) => set('floorNumber', e.target.value)}
            />
          </Field>
        </div>
        <div className="col-6 col-md-4">
          <Field label="Total floors" htmlFor="home-total-floors">
            <input
              id="home-total-floors"
              className="form-control"
              inputMode="numeric"
              value={home.totalFloors}
              onChange={(e) => set('totalFloors', e.target.value)}
            />
          </Field>
        </div>

        <div className="col-6 col-md-4">
          <Field label="Car parking spaces" htmlFor="home-car">
            <input
              id="home-car"
              className="form-control"
              inputMode="numeric"
              value={home.carParkingCount}
              onChange={(e) => set('carParkingCount', e.target.value)}
            />
          </Field>
        </div>
        <div className="col-6 col-md-4">
          <Field label="Two-wheeler spaces" htmlFor="home-bike">
            <input
              id="home-bike"
              className="form-control"
              inputMode="numeric"
              value={home.bikeParkingCount}
              onChange={(e) => set('bikeParkingCount', e.target.value)}
            />
          </Field>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-sm-4">
          <Checkbox id="home-covered" label="Covered parking" checked={home.isCoveredParking} onChange={(v) => set('isCoveredParking', v)} />
        </div>
        <div className="col-12 col-sm-4">
          <Checkbox id="home-lift" label="Lift" checked={home.hasLift} onChange={(v) => set('hasLift', v)} />
        </div>
        <div className="col-12 col-sm-4">
          <Checkbox id="home-power" label="Power backup" checked={home.hasPowerBackup} onChange={(v) => set('hasPowerBackup', v)} />
        </div>
      </div>

      <fieldset className="mt-3">
        <legend className="form-label">Tenant preferences</legend>
        <p className="sf-muted small">
          Who this home is open to. Leave everything unticked, or pick &ldquo;Anyone&rdquo;, if you have no preference.
        </p>

        <div className="row">
          {preferences.map((preference) => (
            <div className="col-12 col-sm-6" key={preference.id}>
              <Checkbox
                id={`pref-${preference.id}`}
                label={preference.name}
                checked={selectedPreferences.includes(preference.id)}
                onChange={(checked) =>
                  set(
                    'tenantPreferenceIds',
                    checked
                      ? [...selectedPreferences, preference.id]
                      : selectedPreferences.filter((id) => id !== preference.id),
                  )
                }
              />
            </div>
          ))}
        </div>

        <Field label="Anything else about who can rent this?" htmlFor="home-pref-notes">
          <textarea
            id="home-pref-notes"
            className="form-control"
            rows={2}
            value={home.tenantPreferenceNotes}
            onChange={(e) => set('tenantPreferenceNotes', e.target.value)}
            maxLength={1000}
          />
        </Field>
      </fieldset>
    </section>
  );
}

function HotelSection({
  hotel,
  setHotel,
}: {
  hotel: HotelForm;
  setHotel: Dispatch<SetStateAction<HotelForm>>;
}) {
  const set = <K extends keyof HotelForm>(key: K, value: HotelForm[K]) =>
    setHotel((previous) => ({ ...previous, [key]: value }));

  return (
    <section className="sf-card sf-card-pad mb-3">
      <h3 className="sf-section-title">Hotel details</h3>

      <div className="row">
        <div className="col-12 col-md-6">
          <Field label="Hotel type" htmlFor="hotel-type" required>
            <select
              id="hotel-type"
              className="form-select"
              value={hotel.hotelType}
              onChange={(e) => set('hotelType', e.target.value)}
            >
              <option value="Budget">Budget</option>
              <option value="Business">Business</option>
              <option value="Premium">Premium</option>
              <option value="Resort">Resort</option>
              <option value="ServiceApartment">Service apartment</option>
              <option value="Other">Other</option>
            </select>
          </Field>
        </div>

        <div className="col-12 col-md-6">
          <Field
            label="Star rating"
            htmlFor="hotel-stars"
            hint="Shown to customers as your own statement until RoomKart verifies it."
          >
            <select
              id="hotel-stars"
              className="form-select"
              value={hotel.starRating}
              onChange={(e) => set('starRating', e.target.value)}
            >
              <option value="">Not stated</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n} star
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="col-6 col-md-3">
          <Field label="Check-in time" htmlFor="hotel-checkin" required>
            <input
              id="hotel-checkin"
              type="time"
              className="form-control"
              value={hotel.checkInTime}
              onChange={(e) => set('checkInTime', e.target.value)}
            />
          </Field>
        </div>

        <div className="col-6 col-md-3">
          <Field label="Check-out time" htmlFor="hotel-checkout" required>
            <input
              id="hotel-checkout"
              type="time"
              className="form-control"
              value={hotel.checkOutTime}
              onChange={(e) => set('checkOutTime', e.target.value)}
            />
          </Field>
        </div>
      </div>

      <Checkbox
        id="hotel-reception"
        label="Reception is staffed 24 hours"
        checked={hotel.is24HourReception}
        onChange={(v) => set('is24HourReception', v)}
      />

      {!hotel.is24HourReception && (
        <Field label="Reception hours" htmlFor="hotel-reception-hours">
          <input
            id="hotel-reception-hours"
            className="form-control"
            value={hotel.receptionHours}
            onChange={(e) => set('receptionHours', e.target.value)}
            placeholder="7 AM to 11 PM"
          />
        </Field>
      )}

      <Field label="Cancellation policy" htmlFor="hotel-cancellation">
        <textarea
          id="hotel-cancellation"
          className="form-control"
          rows={2}
          value={hotel.cancellationPolicy}
          onChange={(e) => set('cancellationPolicy', e.target.value)}
          maxLength={2000}
        />
      </Field>

      <Field label="Policy for children" htmlFor="hotel-children">
        <textarea
          id="hotel-children"
          className="form-control"
          rows={2}
          value={hotel.childPolicy}
          onChange={(e) => set('childPolicy', e.target.value)}
          maxLength={1000}
        />
      </Field>

      <div className="row">
        <div className="col-12 col-sm-6 col-lg-3">
          <Checkbox id="hotel-couples" label="Couples welcome" checked={hotel.couplesAllowed} onChange={(v) => set('couplesAllowed', v)} />
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <Checkbox id="hotel-localid" label="Local ID accepted" checked={hotel.localIdAccepted} onChange={(v) => set('localIdAccepted', v)} />
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <Checkbox id="hotel-pets" label="Pets allowed" checked={hotel.petsAllowed} onChange={(v) => set('petsAllowed', v)} />
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <Checkbox id="hotel-restaurant" label="Restaurant" checked={hotel.hasRestaurant} onChange={(v) => set('hasRestaurant', v)} />
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <Checkbox id="hotel-roomservice" label="Room service" checked={hotel.hasRoomService} onChange={(v) => set('hasRoomService', v)} />
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <Checkbox id="hotel-parking" label="Parking" checked={hotel.hasParking} onChange={(v) => set('hasParking', v)} />
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <Checkbox id="hotel-conference" label="Conference room" checked={hotel.hasConferenceRoom} onChange={(v) => set('hasConferenceRoom', v)} />
        </div>
      </div>
    </section>
  );
}

function ChargesSection({
  charges,
  setCharges,
  category,
}: {
  charges: ChargeRow[];
  setCharges: (rows: ChargeRow[]) => void;
  category: AccommodationCategory;
}) {
  const update = (index: number, changes: Partial<ChargeRow>) =>
    setCharges(charges.map((row, i) => (i === index ? { ...row, ...changes } : row)));

  return (
    <section className="sf-card sf-card-pad mb-3">
      <h3 className="sf-section-title">Additional charges</h3>
      <p className="sf-muted small">
        Anything a customer pays on top of the rent. Mandatory charges are added to the total we show them, so they
        are not surprised later.
      </p>

      {charges.map((charge, index) => (
        <div className="row g-2 align-items-end mb-2" key={index}>
          <div className="col-12 col-sm-4">
            <label className="form-label small mb-1" htmlFor={`charge-label-${index}`}>
              Charge
            </label>
            <input
              id={`charge-label-${index}`}
              className="form-control form-control-sm"
              value={charge.label}
              onChange={(e) => update(index, { label: e.target.value })}
              placeholder="Wi-Fi, laundry, admission fee…"
            />
          </div>

          <div className="col-6 col-sm-2">
            <label className="form-label small mb-1" htmlFor={`charge-amount-${index}`}>
              Amount
            </label>
            <input
              id={`charge-amount-${index}`}
              className="form-control form-control-sm"
              inputMode="numeric"
              value={charge.amount}
              onChange={(e) => update(index, { amount: Number(e.target.value) || 0 })}
            />
          </div>

          <div className="col-6 col-sm-3">
            <label className="form-label small mb-1" htmlFor={`charge-freq-${index}`}>
              How often
            </label>
            <select
              id={`charge-freq-${index}`}
              className="form-select form-select-sm"
              value={charge.frequency}
              onChange={(e) => update(index, { frequency: e.target.value })}
            >
              <option value="Monthly">Per month</option>
              <option value="OneTime">One time</option>
              {category === 'Hotel' && <option value="PerNight">Per night</option>}
              {category === 'Hotel' && <option value="PerStay">Per stay</option>}
            </select>
          </div>

          <div className="col-8 col-sm-2">
            <Checkbox
              id={`charge-mandatory-${index}`}
              label="Mandatory"
              checked={charge.isMandatory}
              onChange={(v) => update(index, { isMandatory: v })}
            />
          </div>

          <div className="col-4 col-sm-1">
            <button
              type="button"
              className="btn btn-sm btn-outline-danger w-100"
              onClick={() => setCharges(charges.filter((_, i) => i !== index))}
              aria-label={`Remove ${charge.label || 'charge'}`}
            >
              <IconClose />
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        onClick={() => setCharges([...charges, { label: '', amount: 0, frequency: 'Monthly', isMandatory: true }])}
      >
        Add a charge
      </button>
    </section>
  );
}
